import React, { useState, useEffect, useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { nanoid } from "nanoid";
import { apiPost, apiGet, isOnline } from "@/lib/api";
import { enqueue, SYNC_PRIORITY } from "@/lib/sync-queue";
import { useOfflineFlha } from "@/hooks/use-offline-flha";
import { useSession } from "@/lib/auth-client";
import {
  useFlhaWizardStore,
  useFlhaWizardHydrated,
} from "@/stores/flha-wizard-store";
import { WizardShell } from "@/components/flha/wizard-shell";
import { JobSetupStep } from "@/components/flha/steps/job-setup-step";
import { TasksStep } from "@/components/flha/steps/tasks-step";
import { HighRiskStep } from "@/components/flha/steps/high-risk-step";
import { HazardsControlsPpeStep } from "@/components/flha/steps/hazards-controls-ppe-step";
import { ReviewScreen } from "@/components/flha/review/review-screen";
import { SubmitSuccess } from "@/components/flha/submit-success";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { X } from "lucide-react";

export const Route = createFileRoute("/_wizard/flha/new")({
  component: FlhaWizard,
});

// -- Task catalog type --------------------------------------------------------

interface TaskCatalogItem {
  id: string;
  isHighRisk: boolean;
  highRiskType: string | null;
}

// -- Dynamic steps ------------------------------------------------------------

function useSteps() {
  const hasHighRisk = useFlhaWizardStore((s) => s.hasHighRiskTasks);
  return useMemo(() => {
    const steps: React.ComponentType[] = [JobSetupStep, TasksStep];
    if (hasHighRisk) steps.push(HighRiskStep);
    steps.push(HazardsControlsPpeStep);
    return steps;
  }, [hasHighRisk]);
}

function WizardSkeleton() {
  return (
    <div className="max-w-lg mx-auto space-y-4 pt-6">
      <div className="flex items-center justify-between px-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center flex-1 last:flex-none">
            <Skeleton className="h-7 w-7 rounded-full" />
            {i < 2 && <Skeleton className="h-0.5 flex-1 mx-1" />}
          </div>
        ))}
      </div>
      <Skeleton className="h-4 w-24 mx-auto" />
      <div className="space-y-3 px-4 pt-4">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    </div>
  );
}

/**
 * Step validation: returns whether the current step data is complete enough to proceed.
 */
/**
 * Step validation for merged wizard layout:
 *   0 = Job Setup (setup+team+site) — project required
 *   1 = Tasks — at least 1 task
 *   [2 = High-Risk — conditional, always allows advance]
 *   2/3 = Hazards+Controls+PPE — combined validation
 */
function useStepValidation(step: number): boolean {
  const projectId = useFlhaWizardStore((s) => s.projectId);
  const selectedTaskIds = useFlhaWizardStore((s) => s.selectedTaskIds);
  const hazards = useFlhaWizardStore((s) => s.hazards);
  const ppeItems = useFlhaWizardStore((s) => s.ppeItems);
  const ppeInspected = useFlhaWizardStore((s) => s.ppeInspected);
  const hasHighRisk = useFlhaWizardStore((s) => s.hasHighRiskTasks);

  const hazardsPpeStep = hasHighRisk ? 3 : 2;

  switch (step) {
    case 0: // Job Setup: project is required
      return !!projectId;
    case 1: // Tasks: at least 1 selected
      return selectedTaskIds.length > 0;
    case 2: {
      if (hasHighRisk) {
        // High-risk step: always allow advance (completion checked on review)
        return true;
      }
      // Fall through to hazards+controls+ppe validation
      return validateHazardsControlsPpe(hazards, ppeItems, ppeInspected);
    }
    default: {
      if (step === hazardsPpeStep) {
        return validateHazardsControlsPpe(hazards, ppeItems, ppeInspected);
      }
      return true;
    }
  }
}

function validateHazardsControlsPpe(
  hazards: { selected: boolean; riskBefore: string | null; riskAfter: string | null; selectedControlIds: string[]; customControls: string[] }[],
  ppeItems: { confirmed: boolean }[],
  ppeInspected: boolean
): boolean {
  const selectedHazards = hazards.filter((h) => h.selected);

  // Hazards: all need riskBefore
  if (selectedHazards.length > 0 && !selectedHazards.every((h) => h.riskBefore !== null)) {
    return false;
  }

  // Controls: all need at least 1 control + riskAfter
  if (
    selectedHazards.length > 0 &&
    !selectedHazards.every(
      (h) =>
        (h.selectedControlIds.length > 0 ||
          (h.customControls && h.customControls.filter((c) => c.trim()).length > 0)) &&
        h.riskAfter !== null
    )
  ) {
    return false;
  }

  // PPE: all confirmed + inspected
  if (ppeItems.length > 0 && !(ppeItems.every((p) => p.confirmed) && ppeInspected)) {
    return false;
  }

  return true;
}

function FlhaWizard() {
  const hydrated = useFlhaWizardHydrated();
  const currentStep = useFlhaWizardStore((s) => s.currentStep);
  const flhaId = useFlhaWizardStore((s) => s.flhaId);
  const projectName = useFlhaWizardStore((s) => s.projectName);
  const setFlhaId = useFlhaWizardStore((s) => s.setFlhaId);
  const setStep = useFlhaWizardStore((s) => s.setStep);
  const selectedTaskIds = useFlhaWizardStore((s) => s.selectedTaskIds);
  const setHasHighRiskTasks = useFlhaWizardStore((s) => s.setHasHighRiskTasks);
  const canNext = useStepValidation(currentStep);
  const navigate = useNavigate();
  const [showDiscard, setShowDiscard] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedFlhaId, setSubmittedFlhaId] = useState<string | null>(null);
  const [submittedProjectName, setSubmittedProjectName] = useState("");
  const { saveDraft: saveLocalDraft } = useOfflineFlha();
  const { data: session } = useSession();

  const steps = useSteps();

  // Fetch task catalog to detect high-risk tasks
  const { data: tasksData } = useQuery({
    queryKey: ["catalogs", "tasks"],
    queryFn: () =>
      apiGet<{ data: TaskCatalogItem[] }>("/api/catalogs/tasks"),
    staleTime: 5 * 60 * 1000,
  });

  // Update hasHighRiskTasks when selected tasks change
  useEffect(() => {
    if (!tasksData?.data) return;
    const hasHighRisk = tasksData.data.some(
      (t) => selectedTaskIds.includes(t.id) && t.isHighRisk
    );
    setHasHighRiskTasks(hasHighRisk);
  }, [selectedTaskIds, tasksData, setHasHighRiskTasks]);

  // Create FLHA mutation (fired when leaving step 0 for the first time)
  // Handles both online (API call) and offline (client-side ID + queue) paths
  const createFlhaMutation = useMutation({
    mutationFn: async () => {
      const state = useFlhaWizardStore.getState();
      const createBody = {
        projectId: state.projectId,
        crewId: state.crewIds[0] || null,
        workArea: state.workArea,
        shift: state.shift,
      };

      if (!isOnline()) {
        // OFFLINE-01: Generate client-side ID and save locally
        const clientId = nanoid();
        const userId = session?.user?.id || "unknown";
        await saveLocalDraft(clientId, null, state, userId);
        // Enqueue the server creation for sync
        await enqueue(
          "POST",
          "/api/flha",
          { ...createBody, id: clientId },
          clientId,
          SYNC_PRIORITY.FLHA_DRAFT
        );
        return { data: { id: clientId, supervisorUserId: null } };
      }

      return apiPost<{
        data: { id: string; supervisorUserId: string | null };
      }>("/api/flha", createBody);
    },
    onSuccess: (res) => {
      setFlhaId(res.data.id);
      if (res.data.supervisorUserId) {
        useFlhaWizardStore
          .getState()
          .updateSetup({ supervisorUserId: res.data.supervisorUserId });
      }
    },
    onError: (err) => {
      toast.error(
        err instanceof Error ? err.message : "Failed to create FLHA"
      );
    },
  });

  const handleDiscard = () => {
    useFlhaWizardStore.getState().reset();
    localStorage.removeItem("flha-wizard-draft");
    setShowDiscard(false);
    navigate({ to: "/" });
  };

  if (!hydrated) return <WizardSkeleton />;

  // Submit success screen — use local state (store gets reset on mount)
  if (submitted && submittedFlhaId) {
    return (
      <SubmitSuccess
        flhaId={submittedFlhaId}
        projectName={submittedProjectName || "Project"}
        date={new Date().toLocaleDateString()}
      />
    );
  }

  // Review screen (pre-submit)
  if (reviewMode && flhaId) {
    return (
      <ReviewScreen
        flhaId={flhaId}
        onEditSection={(stepIndex) => {
          setReviewMode(false);
          setStep(stepIndex);
        }}
        onSubmitSuccess={() => {
          setSubmittedFlhaId(flhaId);
          setSubmittedProjectName(projectName || "Project");
          setSubmitted(true);
        }}
      />
    );
  }

  const StepComponent = steps[currentStep];

  // onBeforeNext: create FLHA on server when leaving setup step
  const handleBeforeNext = async (): Promise<boolean> => {
    if (currentStep === 0 && !flhaId) {
      try {
        await createFlhaMutation.mutateAsync();
        return true;
      } catch {
        return false;
      }
    }
    return true;
  };

  return (
    <>
      {/* Discard button in top-right corner */}
      <div className="max-w-lg mx-auto flex justify-end pt-2 pr-2">
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-destructive"
          onClick={() => setShowDiscard(true)}
        >
          <X className="h-4 w-4 mr-1" />
          Cancel
        </Button>
      </div>

      <WizardShell
        canNext={canNext}
        onBeforeNext={handleBeforeNext}
        onReview={() => setReviewMode(true)}
      >
        <StepComponent />
      </WizardShell>

      {/* Discard confirmation dialog */}
      <AlertDialog open={showDiscard} onOpenChange={setShowDiscard}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard this draft?</AlertDialogTitle>
            <AlertDialogDescription>
              Your progress will be saved on the server, but the local draft will
              be cleared.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={handleDiscard}
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
